/**
 * Sidenav item styling.
 *
 * White sidebar (set in SidenavRoot) needs visible active + hover
 * states that don't rely on shadow. Approach:
 *  - Active row: tinted primary background (~10% alpha) + primary text
 *    + a 3px primary indicator bar on the left
 *  - Hover: very light primary tint (~5% alpha)
 *  - Active icon box: solid primary with white icon
 *  - Inactive icon box: light slate surface with slate icon
 *
 * The `color` prop on SidenavCollapse is honored only when explicitly
 * set to something other than the default — the default active color
 * is always the theme primary so brand stays consistent.
 */

function collapseItem(theme, ownerState) {
  const { palette, transitions, borders, functions } = theme;
  const { active } = ownerState;

  const { primary, text, transparent } = palette;
  const { borderRadius } = borders;
  const { pxToRem, rgba } = functions;

  return {
    position: "relative",
    background: active ? rgba(primary.main, 0.1) : transparent.main,
    color: active ? primary.main : text.main,
    display: "flex",
    alignItems: "center",
    width: "100%",
    padding: `${pxToRem(10)} ${pxToRem(12.8)}`,
    margin: `${pxToRem(2)} ${pxToRem(12)}`,
    borderRadius: borderRadius.md,
    cursor: "pointer",
    userSelect: "none",
    whiteSpace: "nowrap",
    boxShadow: "none",
    transition: transitions.create(["background-color", "color"], {
      easing: transitions.easing.easeInOut,
      duration: transitions.duration.shorter,
    }),
    "&:hover": {
      backgroundColor: active ? rgba(primary.main, 0.14) : rgba(primary.main, 0.05),
    },
    ...(active && {
      "&::before": {
        content: '""',
        position: "absolute",
        left: pxToRem(-4),
        top: pxToRem(10),
        bottom: pxToRem(10),
        width: pxToRem(3),
        borderRadius: pxToRem(2),
        backgroundColor: primary.main,
      },
    }),
  };
}

function collapseIconBox(theme, ownerState) {
  const { palette, transitions, borders, functions } = theme;
  const { active, color } = ownerState;

  const { primary, white, light } = palette;
  const { borderRadius } = borders;
  const { pxToRem } = functions;

  const activeBg = color && color !== "default" && palette[color] ? palette[color].main : primary.main;

  return {
    background: active ? activeBg : light.main,
    minWidth: pxToRem(32),
    minHeight: pxToRem(32),
    borderRadius: borderRadius.md,
    display: "grid",
    placeItems: "center",
    boxShadow: "none",
    transition: transitions.create(["background-color", "color"], {
      easing: transitions.easing.easeInOut,
      duration: transitions.duration.standard,
    }),
    "& svg, svg g": {
      fill: active ? white.main : palette.grey[700],
    },
  };
}

const collapseIcon = ({ palette: { white, grey } }, { active }) => ({
  color: active ? white.main : grey[700],
});

function collapseText(theme, ownerState) {
  const { typography, transitions, breakpoints, functions } = theme;
  const { miniSidenav, transparentSidenav, active } = ownerState;

  const { size, fontWeightBold, fontWeightRegular } = typography;
  const { pxToRem } = functions;

  return {
    marginLeft: pxToRem(12.8),

    [breakpoints.up("xl")]: {
      opacity: miniSidenav || (miniSidenav && transparentSidenav) ? 0 : 1,
      maxWidth: miniSidenav || (miniSidenav && transparentSidenav) ? 0 : "100%",
      marginLeft: miniSidenav || (miniSidenav && transparentSidenav) ? 0 : pxToRem(12.8),
      transition: transitions.create(["opacity", "margin"], {
        easing: transitions.easing.easeInOut,
        duration: transitions.duration.standard,
      }),
    },

    "& span": {
      fontWeight: active ? fontWeightBold : fontWeightRegular,
      fontSize: size.sm,
      lineHeight: 1,
    },
  };
}

export { collapseItem, collapseIconBox, collapseIcon, collapseText };
